import { useState, useEffect, useMemo, useCallback } from 'react'
import { goalsService, suggestionService } from '../services/AdminServices'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import {
  Target, Plus, Trash2, Save, X, Edit3, AlertTriangle,
  BarChart2, CheckCircle, MessageSquare, User, Mail, Clock, Calendar, Users, DollarSign, CalendarDays, Pin
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import './GoalsHub.css'

const GOAL_CATEGORIES = [
  { value: 'all', label: 'Todas', icon: '📋' },
  { value: 'operativo', label: 'Operativo', icon: '⚙️' },
  { value: 'producto', label: 'Producto', icon: '📦' },
  { value: 'soporte', label: 'Soporte', icon: '🎧' },
  { value: 'comercial', label: 'Comercial', icon: '💰' },
  { value: 'desarrollo', label: 'Desarrollo', icon: '🔧' },
]

const METRICS = [
  // ── Operativo ──
  { value: 'installations', label: 'Instalaciones', unit: '', color: '#38bdf8', category: 'operativo' },
  { value: 'uptime', label: 'Uptime Dispositivos', unit: '%', color: '#4ade80', category: 'operativo' },
  { value: 'devices_online', label: 'Dispositivos Online', unit: '', color: '#34d399', category: 'operativo' },
  { value: 'avg_response_time', label: 'Tiempo Resp. Promedio', unit: 'min', color: '#60a5fa', category: 'operativo' },
  // ── Producto ──
  { value: 'devices_sold', label: 'Dispositivos Vendidos', unit: '', color: '#a78bfa', category: 'producto' },
  { value: 'firmware_updates', label: 'Actualizaciones FW', unit: '', color: '#c084fc', category: 'producto' },
  { value: 'new_features', label: 'Features Lanzadas', unit: '', color: '#e879f9', category: 'producto' },
  { value: 'bugs_fixed', label: 'Bugs Corregidos', unit: '', color: '#f472b6', category: 'producto' },
  // ── Soporte ──
  { value: 'support_tickets', label: 'Tickets Cerrados', unit: '', color: '#fb923c', category: 'soporte' },
  { value: 'avg_resolution', label: 'Resolución Promedio', unit: 'hs', color: '#f59e0b', category: 'soporte' },
  { value: 'csat', label: 'Satisfacción (CSAT)', unit: '%', color: '#fbbf24', category: 'soporte' },
  { value: 'nps', label: 'NPS Score', unit: '', color: '#a78bfa', category: 'soporte' },
  // ── Comercial ──
  { value: 'revenue', label: 'Facturación', unit: '$', color: '#4ade80', category: 'comercial' },
  { value: 'new_clients', label: 'Nuevos Clientes', unit: '', color: '#60a5fa', category: 'comercial' },
  { value: 'subscriptions', label: 'Suscripciones Activas', unit: '', color: '#f59e0b', category: 'comercial' },
  { value: 'mrr', label: 'MRR', unit: '$', color: '#4ade80', category: 'comercial' },
  { value: 'churn', label: 'Churn Rate', unit: '%', color: '#f87171', category: 'comercial' },
  // ── Desarrollo ──
  { value: 'tasks_completed', label: 'Tareas Completadas', unit: '', color: '#6ee7b7', category: 'desarrollo' },
  { value: 'deploy_count', label: 'Deploys Realizados', unit: '', color: '#67e8f9', category: 'desarrollo' },
  { value: 'docs_written', label: 'Docs Escritas', unit: '', color: '#a5b4fc', category: 'desarrollo' },
  { value: 'custom', label: 'Personalizada', unit: '', color: '#94a3b8', category: 'operativo' },
]

const SUGGESTION_STATUSES = [
  { value: 'pending', label: 'Pendiente', color: '#f59e0b', icon: '⏳' },
  { value: 'reviewing', label: 'En revisión', color: '#60a5fa', icon: '👀' },
  { value: 'accepted', label: 'Aceptada', color: '#34d399', icon: '✅' },
  { value: 'in_progress', label: 'En desarrollo', color: '#a78bfa', icon: '🚀' },
  { value: 'completed', label: 'Completada', color: '#10b981', icon: '🎉' },
  { value: 'rejected', label: 'Descartada', color: '#64748b', icon: '❌' },
]

const SUGGESTION_TYPE_LABELS = {
  feature: '✨ Nueva función',
  improvement: '🔧 Mejora',
  bug: '🐛 Bug',
  ui: '🎨 Diseño',
  other: '💬 Otro',
}

const SUGGESTION_PRIORITIES = [
  { value: 'low', label: 'Baja', color: '#64748b' },
  { value: 'medium', label: 'Media', color: '#f59e0b' },
  { value: 'high', label: 'Alta', color: '#f87171' },
]

function getCurrentPeriod() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function formatValue(val, unit) {
  if (unit === '$') return '$' + Number(val || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })
  if (unit === '%') return (val || 0) + '%'
  if (unit === 'hs') return (val || 0) + ' hs'
  if (unit === 'min') return (val || 0) + ' min'
  return val || 0
}

function getMetricInfo(metric) {
  return METRICS.find(m => m.value === metric) || METRICS[0]
}

// ── Encode/decode extra fields into notes (backend only saves: metric, target, current, unit, notes, period) ──
const META_PREFIX = '%%META%%'
function encodeGoalMeta(notes, extra) {
  const meta = JSON.stringify(extra)
  return `${META_PREFIX}${meta}${META_PREFIX}\n${notes}`
}
function decodeGoalMeta(rawNotes) {
  if (!rawNotes || !rawNotes.startsWith(META_PREFIX)) return { notes: rawNotes || '', meta: {} }
  const endIdx = rawNotes.indexOf(META_PREFIX, META_PREFIX.length)
  if (endIdx === -1) return { notes: rawNotes, meta: {} }
  try {
    const meta = JSON.parse(rawNotes.substring(META_PREFIX.length, endIdx))
    const notes = rawNotes.substring(endIdx + META_PREFIX.length).replace(/^\n/, '')
    return { notes, meta }
  } catch { return { notes: rawNotes, meta: {} } }
}
function hydrateGoal(goal) {
  const { notes, meta } = decodeGoalMeta(goal.notes)
  return { ...goal, notes, ...meta }
}

export default function GoalsHub() {
  const { user } = useAuth()
  const [tab, setTab] = useState('goals') // 'goals' | 'suggestions'
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [period, setPeriod] = useState(getCurrentPeriod())
  const [catFilter, setCatFilter] = useState('all')
  const [viewMode, setViewMode] = useState('annual') // 'monthly' | 'annual'
  const [year, setYear] = useState(() => getCurrentPeriod().split('-')[0])
  const [annualGoals, setAnnualGoals] = useState([])
  const [annualLoading, setAnnualLoading] = useState(false)
  const [adminUsers, setAdminUsers] = useState([])
  const [selectedOwners, setSelectedOwners] = useState([]) // empty = all
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false)
  const [form, setForm] = useState({
    period: getCurrentPeriod(), metric: 'installations', target: '', current: '', unit: '', notes: '', customLabel: '', owner: '',
    goalType: 'monthly', specificDate: '', totalValue: ''
  })

  // Suggestions state
  const [suggestions, setSuggestions] = useState([])
  const [sugLoading, setSugLoading] = useState(false)
  const [sugFilter, setSugFilter] = useState('all')
  const [editingNote, setEditingNote] = useState(null)
  const [noteText, setNoteText] = useState('')

  const loadGoals = useCallback(async () => {
    try {
      setLoading(true)
      const res = await goalsService.list({ period })
      setGoals((res.goals || res.documents || []).map(hydrateGoal))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { loadGoals() }, [loadGoals])

  useEffect(() => {
    adminApi.get('/admin/admin-users').then(r => setAdminUsers(r.users || [])).catch(() => {})
  }, [])

  const loadAnnualGoals = useCallback(async () => {
    setAnnualLoading(true)
    try {
      const res = await goalsService.list({})
      setAnnualGoals((res.goals || res.documents || []).map(hydrateGoal))
    } catch (e) {
      setError(e.message)
    } finally {
      setAnnualLoading(false)
    }
  }, [])

  useEffect(() => { if (viewMode === 'annual') loadAnnualGoals() }, [viewMode, loadAnnualGoals])

  // ── Suggestions ──
  const loadSuggestions = useCallback(async () => {
    setSugLoading(true)
    try {
      const params = sugFilter !== 'all' ? { status: sugFilter } : {}
      const res = await suggestionService.list(params)
      setSuggestions(res.suggestions || [])
    } catch (e) { setError(e.message) }
    finally { setSugLoading(false) }
  }, [sugFilter])

  // Load suggestions on mount + when tab/filter changes
  useEffect(() => { loadSuggestions() }, [loadSuggestions])

  async function updateSuggestionStatus(id, status) {
    try {
      await suggestionService.update(id, { status })
      loadSuggestions()
    } catch (e) { setError(e.message) }
  }

  async function saveSuggestionNote(id) {
    try {
      await suggestionService.update(id, { adminNotes: noteText })
      setEditingNote(null)
      setNoteText('')
      loadSuggestions()
    } catch (e) { setError(e.message) }
  }

  async function updateSuggestionPriority(id, priority) {
    try {
      await suggestionService.update(id, { priority })
      loadSuggestions()
    } catch (e) { setError(e.message) }
  }

  async function deleteSuggestion(id) {
    if (!confirm('¿Eliminar esta sugerencia?')) return
    try { await suggestionService.delete(id); loadSuggestions() } catch (e) { setError(e.message) }
  }

  const sugStats = useMemo(() => {
    const total = suggestions.length
    const pending = suggestions.filter(s => s.status === 'pending').length
    const accepted = suggestions.filter(s => ['accepted', 'in_progress', 'completed'].includes(s.status)).length
    return { total, pending, accepted }
  }, [suggestions])

  const allSelected = selectedOwners.length === 0 || (adminUsers.length > 0 && selectedOwners.length === adminUsers.length)
  const isMineOnly = selectedOwners.length === 1 && selectedOwners[0] === user?.email

  function toggleOwner(email) {
    setSelectedOwners(prev => {
      const current = prev.length === 0 ? adminUsers.map(u => u.email) : [...prev]
      return current.includes(email) ? current.filter(e => e !== email) : [...current, email]
    })
  }

  function toggleMine() {
    if (isMineOnly) {
      setSelectedOwners([])
    } else {
      setSelectedOwners([user?.email].filter(Boolean))
    }
  }

  function selectAll() {
    setSelectedOwners([])
  }

  // Filtered goals by category + owner
  const filteredGoals = useMemo(() => {
    let list = goals
    if (catFilter !== 'all') {
      list = list.filter(g => {
        const info = getMetricInfo(g.metric)
        return info.category === catFilter
      })
    }
    if (selectedOwners.length > 0 && adminUsers.length > 0 && selectedOwners.length < adminUsers.length) {
      list = list.filter(g => selectedOwners.includes(g.owner))
    }
    return list
  }, [goals, catFilter, selectedOwners, adminUsers])

  // Stats (based on filtered)
  const stats = useMemo(() => {
    const total = filteredGoals.length
    const achieved = filteredGoals.filter(g => g.current >= g.target && g.target > 0).length
    const avgProgress = filteredGoals.length > 0
      ? filteredGoals.reduce((sum, g) => sum + Math.min(100, g.target > 0 ? (g.current / g.target) * 100 : 0), 0) / filteredGoals.length
      : 0
    return { total, achieved, avgProgress: avgProgress.toFixed(0) }
  }, [filteredGoals])

  // Chart data (based on filtered)
  const chartData = useMemo(() => {
    return filteredGoals.map(g => {
      const info = getMetricInfo(g.metric)
      const progress = g.target > 0 ? Math.min(100, (g.current / g.target) * 100) : 0
      return {
        name: info.label,
        progress: Number(progress.toFixed(0)),
        color: progress >= 100 ? '#4ade80' : progress >= 75 ? '#f59e0b' : '#f87171',
      }
    })
  }, [filteredGoals])

  const annualData = useMemo(() => {
    const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    return Array.from({ length: 12 }, (_, i) => {
      const m = String(i + 1).padStart(2, '0')
      const p = `${year}-${m}`
      const monthGoals = annualGoals.filter(g => g.period === p)
      const achieved = monthGoals.filter(g => g.current >= g.target && g.target > 0).length
      const avg = monthGoals.length > 0
        ? monthGoals.reduce((sum, g) => sum + Math.min(100, g.target > 0 ? (g.current / g.target) * 100 : 0), 0) / monthGoals.length
        : 0
      return { period: p, month: MONTHS[i], total: monthGoals.length, achieved, avg: Math.round(avg) }
    })
  }, [annualGoals, year])

  function openForm(goal) {
    if (goal) {
      setEditingId(goal.$id)
      const info = getMetricInfo(goal.metric)
      setForm({
        period: goal.period || getCurrentPeriod(), metric: goal.metric || 'installations',
        target: goal.target || '', current: goal.current || '',
        unit: info.unit || '', notes: goal.notes || '', customLabel: goal.customLabel || '',
        owner: goal.owner || '',
        goalType: goal.goalType || 'monthly', specificDate: goal.specificDate || '', totalValue: goal.totalValue || ''
      })
    } else {
      setEditingId(null)
      setForm({ period, metric: 'installations', target: '', current: '', unit: '', notes: '', customLabel: '', owner: user?.email || '',
        goalType: 'monthly', specificDate: '', totalValue: '' })
    }
    setShowForm(true)
  }

  async function saveGoal() {
    if (!form.metric || !form.target) { setError('Métrica y target son requeridos'); return }
    try {
      const extraMeta = {
        customLabel: form.customLabel || '',
        owner: form.owner || '',
        goalType: form.goalType || 'monthly',
        specificDate: form.goalType === 'daily' ? (form.specificDate || '') : '',
        totalValue: Number(form.totalValue) || 0,
      }
      const data = {
        period: form.period,
        metric: form.metric,
        target: Number(form.target) || 0,
        current: Number(form.current) || 0,
        notes: encodeGoalMeta(form.notes || '', extraMeta),
      }
      if (editingId) await goalsService.update(editingId, data)
      else await goalsService.create(data)
      setShowForm(false)
      setEditingId(null)
      loadGoals()
    } catch (e) { setError(e.message) }
  }

  async function deleteGoal(id) {
    if (!confirm('¿Eliminar esta meta?')) return
    try { await goalsService.delete(id); loadGoals() } catch (e) { setError(e.message) }
  }

  async function updateCurrent(goal, newCurrent) {
    try {
      await goalsService.update(goal.$id, { current: Number(newCurrent) || 0 })
      loadGoals()
    } catch (e) { setError(e.message) }
  }

  function changePeriod(delta) {
    const [y, m] = period.split('-').map(Number)
    const d = new Date(y, m - 1 + delta, 1)
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const periodLabel = (() => {
    const [y, m] = period.split('-').map(Number)
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    return `${months[m - 1]} ${y}`
  })()

  if (loading && goals.length === 0) {
    return <div className="goal-page"><div className="goal-loading"><div className="goal-spinner" /> Cargando metas...</div></div>
  }

  return (
    <div className="goal-page">
      <div className="goal-header">
        <div className="goal-header-left">
          <Target size={28} />
          <div>
            <h1>Metas y Objetivos</h1>
            <p className="goal-subtitle">{tab === 'goals' ? `${periodLabel} — ${stats.achieved}/${stats.total} logradas` : `${sugStats.total} sugerencias · ${sugStats.pending} pendientes`}</p>
          </div>
        </div>
        <div className="goal-header-actions">
          {tab === 'goals' && (
            <>
              <button
                className={`goal-btn ${isMineOnly ? 'goal-btn-primary' : 'goal-btn-secondary'}`}
                onClick={toggleMine}
              >
                <User size={16} /> Mis metas
              </button>
              {adminUsers.length > 0 && (
                <div className="owner-multiselect">
                  <button className="owner-multiselect-trigger" onClick={() => setShowOwnerDropdown(v => !v)}>
                    <Users size={14} />
                    {allSelected ? 'Todo el equipo' : `${selectedOwners.length} seleccionado${selectedOwners.length !== 1 ? 's' : ''}`}
                  </button>
                  {showOwnerDropdown && (
                    <div className="owner-multiselect-dropdown">
                      <div className="owner-multiselect-item" onClick={selectAll}>
                        <input type="checkbox" checked={allSelected} readOnly />
                        <span>👥 Todo el equipo</span>
                      </div>
                      {adminUsers.map(u => {
                        const emails = selectedOwners.length === 0 ? adminUsers.map(x => x.email) : selectedOwners
                        const checked = emails.includes(u.email)
                        return (
                          <div key={u.$id} className="owner-multiselect-item" onClick={() => toggleOwner(u.email)}>
                            <input type="checkbox" checked={checked} readOnly />
                            <span>{u.name || u.email}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {tab === 'goals' && viewMode === 'monthly' && (
            <button className="goal-btn goal-btn-primary" onClick={() => openForm()}>
              <Plus size={16} /> Nueva Meta
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="goal-tabs">
        <button className={tab === 'goals' ? 'active' : ''} onClick={() => setTab('goals')}>
          <Target size={16} /> Metas
        </button>
        <button className={tab === 'suggestions' ? 'active' : ''} onClick={() => setTab('suggestions')}>
          <MessageSquare size={16} /> Sugerencias {sugStats.pending > 0 && <span className="goal-tab-badge">{sugStats.pending}</span>}
        </button>
      </div>

      {error && <div className="goal-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      {/* ══ GOALS TAB ══ */}
      {tab === 'goals' && (<>

      {/* ── Annual view ── */}
      {viewMode === 'annual' && (
        <div className="goal-annual-view">
          <div className="goal-annual-top">
            <button className="goal-back-btn" onClick={() => setViewMode('monthly')}>← Volver al mes</button>
            <div className="goal-year-nav">
              <button onClick={() => setYear(y => String(Number(y) - 1))}>← {Number(year) - 1}</button>
              <span>{year}</span>
              <button onClick={() => setYear(y => String(Number(y) + 1))}>{Number(year) + 1} →</button>
            </div>
          </div>
          {annualLoading ? (
            <div className="goal-loading"><div className="goal-spinner" /> Cargando año...</div>
          ) : (
            <div className="goal-year-grid">
              {annualData.map(m => {
                const color = m.total === 0 ? '#475569'
                  : m.achieved === m.total ? '#4ade80'
                  : m.avg >= 75 ? '#f59e0b' : '#60a5fa'
                const isCurrent = m.period === getCurrentPeriod()
                return (
                  <div key={m.period}
                    className={`goal-month-card ${isCurrent ? 'current' : ''} ${m.total === 0 ? 'empty' : ''}`}
                    onClick={() => { setPeriod(m.period); setViewMode('monthly') }}>
                    <div className="goal-month-name">{m.month}</div>
                    {m.total > 0 ? (<>
                      <div className="goal-month-stats">
                        <span className="goal-month-total">{m.total} {m.total === 1 ? 'meta' : 'metas'}</span>
                        <span className="goal-month-achieved" style={{ color: '#4ade80' }}>{m.achieved} logradas</span>
                      </div>
                      <div className="goal-month-bar">
                        <div className="goal-month-bar-fill" style={{ width: `${m.avg}%`, background: color }} />
                      </div>
                      <div className="goal-month-pct" style={{ color }}>{m.avg}%</div>
                    </>) : (
                      <span className="goal-month-empty-label">Sin metas</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Monthly view ── */}
      {viewMode === 'monthly' && (<>

      {/* Category filter + Period nav */}
      <div className="goal-cat-filters">
        {GOAL_CATEGORIES.map(c => (
          <button key={c.value} className={catFilter === c.value ? 'active' : ''}
            onClick={() => setCatFilter(c.value)}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      <div className="goal-period-nav">
        <button onClick={() => changePeriod(-1)}>← Anterior</button>
        <span>{periodLabel}</span>
        <button onClick={() => changePeriod(1)}>Siguiente →</button>
        <button className="goal-annual-btn" onClick={() => { setYear(period.split('-')[0]); setViewMode('annual') }}>
          <Calendar size={15} /> Vista Anual
        </button>
      </div>

      {/* Stats */}
      <div className="goal-stats">
        <div className="goal-stat">
          <span className="goal-stat-num">{stats.total}</span>
          <span className="goal-stat-label">Metas</span>
        </div>
        <div className="goal-stat">
          <span className="goal-stat-num" style={{ color: '#4ade80' }}>{stats.achieved}</span>
          <span className="goal-stat-label">Logradas</span>
        </div>
        <div className="goal-stat">
          <span className="goal-stat-num" style={{ color: '#60a5fa' }}>{stats.avgProgress}%</span>
          <span className="goal-stat-label">Progreso Prom.</span>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="goal-chart-container">
          <h3><BarChart2 size={18} /> Progreso por Objetivo</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 120, right: 20, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} width={110} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                formatter={(val) => [`${val}%`, 'Progreso']}
              />
              <Bar dataKey="progress" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Goals list */}
      <div className="goal-list">
        {filteredGoals.length === 0 ? (
          <div className="goal-empty-state">
            <Target size={48} />
            <p>No hay metas {catFilter !== 'all' ? `de ${GOAL_CATEGORIES.find(c => c.value === catFilter)?.label}` : 'definidas'} para {periodLabel}</p>
            <button className="goal-btn goal-btn-primary" onClick={() => openForm()}>
              <Plus size={16} /> Crear primera meta
            </button>
          </div>
        ) : filteredGoals.map(goal => {
          const info = getMetricInfo(goal.metric)
          const catInfo = GOAL_CATEGORIES.find(c => c.value === info.category)
          const progress = goal.target > 0 ? Math.min(100, (goal.current / goal.target) * 100) : 0
          const achieved = progress >= 100
          const displayLabel = goal.metric === 'custom' && goal.customLabel ? goal.customLabel : info.label
          const isDaily = goal.goalType === 'daily'
          const earnedValue = goal.totalValue ? Math.round((progress / 100) * goal.totalValue) : 0
          return (
            <div key={goal.$id} className={`goal-card ${achieved ? 'achieved' : ''}`}>
              <div className="goal-card-left">
                <div className="goal-card-icon" style={{ color: info.color }}>
                  {achieved ? <CheckCircle size={24} /> : <Target size={24} />}
                </div>
                <div className="goal-card-info">
                  <div className="goal-card-metric">
                    {displayLabel}
                    {catInfo && <span className="goal-cat-badge" style={{ color: info.color }}>{catInfo.icon}</span>}
                    <span className={`goal-type-badge ${isDaily ? 'daily' : 'monthly'}`}>
                      {isDaily ? <><Pin size={11} /> {goal.specificDate ? new Date(goal.specificDate + 'T12:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'short' }) : 'Día puntual'}</> : <><CalendarDays size={11} /> Mensual</>}
                    </span>
                    {goal.owner && (
                      <span className="goal-owner-badge">
                        <User size={11} /> {adminUsers.find(u => u.email === goal.owner)?.name || goal.owner.split('@')[0]}
                      </span>
                    )}
                  </div>
                  <div className="goal-card-values">
                    <span className="goal-current">{formatValue(goal.current, info.unit)}</span>
                    <span className="goal-separator">/</span>
                    <span className="goal-target">{formatValue(goal.target, info.unit)}</span>
                  </div>
                  {goal.totalValue > 0 && (
                    <div className="goal-money-row">
                      <DollarSign size={13} />
                      <span className="goal-money-earned">${earnedValue.toLocaleString('es-AR')}</span>
                      <span className="goal-money-sep">/</span>
                      <span className="goal-money-total">${Number(goal.totalValue).toLocaleString('es-AR')}</span>
                    </div>
                  )}
                  {goal.notes && <p className="goal-card-notes">{goal.notes}</p>}
                </div>
              </div>
              <div className="goal-card-right">
                <div className="goal-progress-ring">
                  <svg viewBox="0 0 36 36">
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="#334155" strokeWidth="3" />
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke={achieved ? '#4ade80' : progress >= 75 ? '#f59e0b' : '#60a5fa'}
                      strokeWidth="3" strokeDasharray={`${progress}, 100`} strokeLinecap="round" />
                  </svg>
                  <span className="goal-progress-pct">{progress.toFixed(0)}%</span>
                </div>
                <div className="goal-card-actions">
                  <input className="goal-current-input" type="number" value={goal.current}
                    onChange={e => updateCurrent(goal, e.target.value)} title="Actualizar valor actual" />
                  <button onClick={() => openForm(goal)} title="Editar"><Edit3 size={14} /></button>
                  <button onClick={() => deleteGoal(goal.$id)} title="Eliminar"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      </>)}
      </>)}

      {/* ══ SUGGESTIONS TAB ══ */}
      {tab === 'suggestions' && (
        <div className="sug-section">
          {/* Stats */}
          <div className="goal-stats">
            <div className="goal-stat">
              <span className="goal-stat-num">{sugStats.total}</span>
              <span className="goal-stat-label">Total</span>
            </div>
            <div className="goal-stat">
              <span className="goal-stat-num" style={{ color: '#f59e0b' }}>{sugStats.pending}</span>
              <span className="goal-stat-label">Pendientes</span>
            </div>
            <div className="goal-stat">
              <span className="goal-stat-num" style={{ color: '#4ade80' }}>{sugStats.accepted}</span>
              <span className="goal-stat-label">Aceptadas</span>
            </div>
          </div>

          {/* Filters */}
          <div className="sug-filters">
            {[{ value: 'all', label: 'Todas' }, ...SUGGESTION_STATUSES].map(s => (
              <button key={s.value} className={sugFilter === s.value ? 'active' : ''}
                onClick={() => setSugFilter(s.value)}>
                {s.icon || '📋'} {s.label}
              </button>
            ))}
          </div>

          {sugLoading ? (
            <div className="goal-loading"><div className="goal-spinner" /> Cargando sugerencias...</div>
          ) : suggestions.length === 0 ? (
            <div className="goal-empty-state">
              <MessageSquare size={48} />
              <p>No hay sugerencias {sugFilter !== 'all' ? 'con ese filtro' : 'todavía'}</p>
            </div>
          ) : (
            <div className="sug-list">
              {suggestions.map(s => {
                const st = SUGGESTION_STATUSES.find(x => x.value === s.status) || SUGGESTION_STATUSES[0]
                const pr = SUGGESTION_PRIORITIES.find(x => x.value === s.priority)
                return (
                  <div key={s.$id} className="sug-card">
                    <div className="sug-card-top">
                      <span className="sug-card-type">{SUGGESTION_TYPE_LABELS[s.type] || s.type}</span>
                      <div className="sug-card-meta">
                        {pr && <span className="sug-priority" style={{ color: pr.color, borderColor: pr.color }}>{pr.label}</span>}
                        <select className="sug-status-select"
                          value={s.status}
                          style={{ color: st.color, borderColor: st.color }}
                          onChange={e => updateSuggestionStatus(s.$id, e.target.value)}>
                          {SUGGESTION_STATUSES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <h4 className="sug-card-title">{s.title}</h4>
                    <p className="sug-card-message">{s.message}</p>

                    <div className="sug-card-user">
                      <User size={14} /> <span>{s.userName || 'Usuario'}</span>
                      {s.userEmail && <><Mail size={14} /> <span>{s.userEmail}</span></>}
                      <Clock size={14} /> <span>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                    </div>

                    {/* Priority selector */}
                    <div className="sug-card-actions-row">
                      <div className="sug-priority-selector">
                        <label>Prioridad:</label>
                        {SUGGESTION_PRIORITIES.map(p => (
                          <button key={p.value}
                            className={s.priority === p.value ? 'active' : ''}
                            style={s.priority === p.value ? { background: `${p.color}20`, color: p.color, borderColor: p.color } : {}}
                            onClick={() => updateSuggestionPriority(s.$id, p.value)}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <button className="sug-delete-btn" onClick={() => deleteSuggestion(s.$id)} title="Eliminar">
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Admin notes */}
                    <div className="sug-admin-notes">
                      {editingNote === s.$id ? (
                        <div className="sug-note-edit">
                          <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                            placeholder="Respuesta visible para el usuario..." rows={2} />
                          <div className="sug-note-actions">
                            <button className="goal-btn goal-btn-primary" onClick={() => saveSuggestionNote(s.$id)}>
                              <Save size={14} /> Guardar
                            </button>
                            <button className="goal-btn goal-btn-secondary" onClick={() => setEditingNote(null)}>
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="sug-note-display" onClick={() => { setEditingNote(s.$id); setNoteText(s.adminNotes || '') }}>
                          {s.adminNotes ? (
                            <><MessageSquare size={14} /> <span>{s.adminNotes}</span> <Edit3 size={12} className="sug-note-edit-icon" /></>
                          ) : (
                            <span className="sug-note-placeholder"><MessageSquare size={14} /> Click para agregar respuesta...</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="goal-form-overlay">
          <div className="goal-form">
            <div className="goal-form-header">
              <h2>{editingId ? 'Editar Meta' : 'Nueva Meta'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="goal-form-body">
              <div className="goal-form-row-2col">
                <div className="goal-form-row">
                  <label>Período</label>
                  <input type="month" value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} />
                </div>
                <div className="goal-form-row">
                  <label>Tipo de meta</label>
                  <select value={form.goalType} onChange={e => setForm({ ...form, goalType: e.target.value, specificDate: '' })}>
                    <option value="monthly">📅 Mensual (1 al 30)</option>
                    <option value="daily">📌 Día puntual</option>
                  </select>
                </div>
              </div>
              {form.goalType === 'daily' && (
                <div className="goal-form-row">
                  <label>Fecha específica</label>
                  <input type="date" value={form.specificDate} onChange={e => setForm({ ...form, specificDate: e.target.value })} />
                </div>
              )}
              <div className="goal-form-row-2col">
                <div className="goal-form-row">
                  <label>Métrica</label>
                  <select value={form.metric} onChange={e => {
                    const info = METRICS.find(m => m.value === e.target.value)
                    setForm({ ...form, metric: e.target.value, unit: info?.unit || '', customLabel: '' })
                  }}>
                    {GOAL_CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <optgroup key={cat.value} label={`${cat.icon} ${cat.label}`}>
                        {METRICS.filter(m => m.category === cat.value).map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>
              {form.metric === 'custom' && (
                <div className="goal-form-row">
                  <label>Nombre del objetivo</label>
                  <input type="text" value={form.customLabel || ''}
                    onChange={e => setForm({ ...form, customLabel: e.target.value })}
                    placeholder="Ej: Documentar API, Armar kit de bienvenida..." />
                </div>
              )}
              <div className="goal-form-row">
                <label>Asignar a</label>
                <select value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {adminUsers.map(u => <option key={u.$id} value={u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div className="goal-form-row-2col">
                <div className="goal-form-row">
                  <label>Target</label>
                  <input type="number" value={form.target} onChange={e => setForm({ ...form, target: e.target.value })}
                    placeholder={`Ej: ${form.unit === '$' ? '500000' : '10'}`} />
                </div>
                <div className="goal-form-row">
                  <label>Actual</label>
                  <input type="number" value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} />
                </div>
              </div>
              <div className="goal-form-row">
                <label>Valor total $ (opcional)</label>
                <input type="number" value={form.totalValue} onChange={e => setForm({ ...form, totalValue: e.target.value })}
                  placeholder="Ej: 500000 — se calcula el ganado según progreso" />
              </div>
              <div className="goal-form-row">
                <label>Notas</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Observaciones..." rows={3} />
              </div>
            </div>
            <div className="goal-form-footer">
              <button className="goal-btn goal-btn-secondary" onClick={() => setShowForm(false)}>
                <X size={16} /> Cancelar
              </button>
              <button className="goal-btn goal-btn-primary" onClick={saveGoal}>
                <Save size={16} /> {editingId ? 'Guardar' : 'Crear Meta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
